import {MUIContainer} from '@/components/MUI-Components';
import {DEFI_META_DATA} from '@/util/metaData/crypto';
import dynamic from 'next/dynamic';
import PageHead from '@/components/PageHead';
import HeroVideoBanner from '@/components/Ui-components/CommonSections/HeroVideoBanner';
import {getDefiSchemas} from '@/schemas/pages/decentralized-finance-defi-development/decentralized-finance-defi-development.schema';
import {buildSchemaGraph} from '@/schemas/generators/buildSchemaGraph';

// ─── getStaticProps ───────────────────────────────────────────────────────────
export const getStaticProps = async () => {
    const {
        bannerBottomDefi,
        defiFAQData,
        milestonesDataDefi,
        defiVsTradfiData,
        defiKeyServicesData,
        defiExpertiseCTAData,
        defiTestimonialData,
        defiSolutionsData,
        defiPartnershipData,
        defiTopCardsData,
        defiChecklistData,
        defiProtocolCTAData,
        defiTechCategories,
        defiComplianceData,
        defiProcessData,
        defiCostData,
        defiFinalCTAData
    } = await import('@/util/contents/defi-data');

    const {awardsList, expertsData} = await import('@/util/contents/blockchain-data');
    const {blockchainPortfolio} = await import('@/util/contents/latest-portfolio-data');

    // ── FAQ: merge dynamic defiFAQData + hardcoded FAQ from old body script ──
    const serializedSchema = buildSchemaGraph(getDefiSchemas());

    return {
        props: {
            bannerBottomDefi,
            defiFAQData,
            milestonesDataDefi,
            defiVsTradfiData,
            defiKeyServicesData,
            defiExpertiseCTAData,
            defiTestimonialData,
            defiSolutionsData,
            defiPartnershipData,
            defiTopCardsData,
            defiChecklistData,
            defiProtocolCTAData,
            defiTechCategories,
            defiComplianceData,
            defiProcessData,
            defiCostData,
            defiFinalCTAData,
            awardsList,
            expertsData,
            blockchainPortfolio,
            serializedSchema
        }
    };
};

// ─── Dynamic imports ──────────────────────────────────────────────────────────
const BannerBottom = dynamic(() => import('@/components/Ui-components/banner-bottom'));
const Milestones = dynamic(() => import('@/components/Ui-components/CommonSections/Milestones'));
const LatestPortfolio = dynamic(() => import('@/components/Ui-components/LatestPortfolio'));
const FAQ = dynamic(() => import('@/components/plain-sections/FAQ'));
const LatestInsights = dynamic(() => import('@/components/Ui-components/latest-insights'));
const TopPlatforms = dynamic(() => import('@/components/Ui-components/CommonSections/TopPlatforms'));
const Decoding = dynamic(() => import('@/components/Ui-components/Decoding'));
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
const DeFiVsTradFi = dynamic(() => import('@/components/Ui-components/CommonSections/DeFiVsTradFi'));
const KeyServices = dynamic(() => import('@/components/Ui-components/CommonSections/KeyServices'));
const CTA = dynamic(() => import('@/components/Ui-components/CommonSections/CTA'));
const SolutionsAccordion = dynamic(() => import('@/components/Ui-components/CommonSections/SolutionsAccordion'));
const WhyChooseUs = dynamic(() => import('@/components/Ui-components/CommonSections/WhyChooseUs'));
import {cdnImage} from '@/util/cdnImage';

const subImage = cdnImage('defi/DeFi-Landscape.webp');
const clutchReviews = cdnImage('Blockchain-images/clutch-reviews.webp');
const heroBg = cdnImage('defi/Decentralized-Finance-Development-Services.webp');
const defiKeyImg = cdnImage('defi/DeFi-Development-Services.webp');
const expertiseCtaImg = cdnImage('defi/Right-expertise-cta.webp');
import Testimonials from '@/components/Ui-components/Testimonials';
const Experts = dynamic(() => import('@/components/Ui-components/CommonSections/Experts'));
const TechStack = dynamic(() => import('@/components/Ui-components/CommonSections/TechStack'));
const ComplianceReady = dynamic(() => import('@/components/Ui-components/CommonSections/ComplianceReady'));
const Awards = dynamic(() => import('@/components/Ui-components/CommonSections/Awards'));
const Process = dynamic(() => import('@/components/Ui-components/CommonSections/Process'));
const Cost = dynamic(() => import('@/components/Ui-components/CommonSections/Cost'));

// Migrated from globals.scss `.defi-header-parent` (nested `.decording-main-wrapper
// .spectrum-header-wrapper`, `@media (max-width: 767px) .animated-button`, and
// `.our-service-defi-wrapper .spectrum-header-wrapper`). Sole owner confirmed — no other
// page/component references `.defi-header-parent`. Applied on the page's own top-level Box
// below (this file's own JSX authors it directly), so the nested descendant-selector keys
// reproduce the same CSS descendant-combinator behavior the original global rule had —
// regardless of which child component actually renders the matched descendant, since that
// was already true of the plain-CSS selector (it crossed component boundaries too).
// NOTE: `.decording-main-wrapper .spectrum-header-wrapper` and
// `.our-service-defi-wrapper .spectrum-header-wrapper` have no current consumer within this
// page's rendered tree (neither `Decoding` nor this page renders `.spectrum-header-wrapper`,
// and this page never applies `.our-service-defi-wrapper`) — migrated unchanged per policy,
// not deleted. Only the `.animated-button` media-query rule is currently live (it matches the
// CTA buttons rendered by several child sections within `.defi-header-parent`).
const defiHeaderParentSx = {
    '& .decording-main-wrapper .spectrum-header-wrapper': {
        textAlign: 'left !important'
    },
    '@media (max-width: 767px)': {
        '& .animated-button': {
            padding: '10px 22px !important' // no exact token match — preserved for parity
        }
    },
    '& .our-service-defi-wrapper .spectrum-header-wrapper': {
        paddingBottom: '30px !important' // no exact token match — preserved for parity
    }
} as const;

// Migrated from globals.scss `.defi-decoding img` (max-width only — `.defi-decoding` itself
// carried no direct properties, just this nested `img` rule). `.defi-decoding` is passed as a
// className to the shared `<Decoding>` component, whose own JSX owns the Box that receives it
// — not this page's JSX — so per the CSS Migration Policy this page has no direct DOM owner to
// attach `sx` to for that class. `Decoding` already exposes an `imgSx` merge-point prop for
// exactly this case (pre-existing prop — not a new passthrough added by this migration),
// applied straight to the rendered image element the original nested `img` selector targeted.
const defiDecodingImgSx = {
    maxWidth: '500px !important' // no exact token match — preserved for parity
} as const;

// ─── Component ────────────────────────────────────────────────────────────────
const DefiDevlopment = (props: any) => {
    const {
        openForm,
        serializedSchema,
        bannerBottomDefi,
        defiFAQData,
        milestonesDataDefi,
        defiVsTradfiData,
        defiKeyServicesData,
        defiExpertiseCTAData,
        defiTestimonialData,
        defiSolutionsData,
        defiPartnershipData,
        defiTopCardsData,
        defiChecklistData,
        defiProtocolCTAData,
        defiTechCategories,
        defiComplianceData,
        defiProcessData,
        defiCostData,
        defiFinalCTAData,
        awardsList,
        expertsData,
        blockchainPortfolio
    } = props;

    const DecodingData = [
        'The decentralized finance market is fundamentally reshaping how capital moves, how yield is generated, and how financial infrastructure is owned. For founders, CTOs, and institutional investors, the opportunity to build category-defining protocols has never been more accessible — or more technically demanding.',
        'DeFi development operates under a distinct set of engineering and strategic requirements. Protocol architecture decisions made at the outset determine long-term scalability. Smart contract logic governs real capital, where precision is non-negotiable. Chain selection, tokenomics design, liquidity incentive structures, and compliance readiness each carry compounding implications for protocol performance and market positioning.',
        'The organizations succeeding in this space are those who approach DeFi development with the same rigor applied to institutional financial infrastructure — combining deep protocol expertise,<a href="/blog/security-first-dex-development" target="_blank">security-first</a> engineering</a>, and strategic clarity from day one.'
    ];

    return (
        <Box className="defi-header-parent" sx={defiHeaderParentSx}>
            <PageHead meta={DEFI_META_DATA} url="/decentralized-finance-defi-development" schema={serializedSchema} />

            {/* ── Page sections — all unchanged ── */}
            <HeroVideoBanner
                heading="Decentralized Finance (Defi) Development Services"
                subheading="We engineer audit-ready, secure <a href='/blog/best-defi-platforms'>DeFi platforms</a> including DEXs, lending protocols, and token ecosystems designed for regulatory alignment, capital protection, scalable architecture, and measurable operational performance across global markets."
                primaryButtonText="Discuss Your Vision With Our Expert"
                onPrimaryClick={openForm}
                backgroundImage={heroBg}
            />
            <Box className="home-banner-bottom helper_section_parent">
                <BannerBottom bannerData={bannerBottomDefi} partnerContent="Trusted By" />
            </Box>
            <Box className="top-space bottom-spacing mobile-top-padding mobile-padding-bottom">
                <Milestones
                    heading="Milestones We've Achieved"
                    milestones={milestonesDataDefi}
                    imageSrc={clutchReviews}
                    imageAlt="Clutch reviews"
                />
            </Box>
            <Grid
                item
                xs={12}
                className="top-space decording-main-wrapper mobile-top-padding bottom-spacing mobile-bottom-padding bg-blue-background"
            >
                <MUIContainer>
                    <Decoding
                        className="defi-decoding"
                        imgSx={defiDecodingImgSx}
                        imageSrc={subImage}
                        alt="The DeFi Landscape: Opportunity, Complexity, and What It Takes to Execute"
                        heading="The DeFi Landscape: Opportunity, Complexity, and What It Takes to Execute"
                        subHeading={DecodingData}
                        buttonText="Let's Bring Your DeFi Vision to Market"
                        openForm={openForm}
                    />
                </MUIContainer>
            </Grid>
            <Box className="top-space bottom-spacing mobile-top-padding mobile-padding-bottom">
                <DeFiVsTradFi data={defiVsTradfiData} />
            </Box>
            <Box className="top-space mobile-top-padding bottom-spacing mobile-bottom-padding bg-blue-background">
                <KeyServices
                    heading="DeFi Development Services for Secure, Compliant Financial Infrastructure"
                    subheading="With over 11 years of experience in blockchain engineering and more than 500 complex technology engagements, we help businesses navigate the full DeFi lifecycle with structured, outcome-driven services designed for enterprises, fintech innovators, and institutional investors. Whether you are entering the DeFi space for the first time or scaling an existing protocol, our engagements are built around measurable outcomes at every stage."
                    imageSrc={defiKeyImg}
                    services={defiKeyServicesData}
                />
            </Box>
            <Box className="top-space bottom-spacing mobile-padding-bottom mobile-top-padding">
                <CTA
                    heading={defiExpertiseCTAData.heading}
                    subheading={defiExpertiseCTAData.subheading}
                    bullets={defiExpertiseCTAData.bullets}
                    buttonText={defiExpertiseCTAData.buttonText}
                    imageSrc={expertiseCtaImg}
                    imageAlt="DeFi Architecture Visual"
                    openForm={openForm}
                />
            </Box>
            <Box className="top-space mobile-top-padding bottom-spacing mobile-bottom-padding bg-blue-background">
                <LatestPortfolio
                    heading="Debut Infotech's Featured Projects"
                    contentData={blockchainPortfolio}
                    showFilters={false}
                    initialCount={6}
                    openForm={openForm}
                    variant="homepage"
                />
            </Box>
            <Box>
                <Testimonials variant="blockchain" heading="What Our Clients Say" clientData={defiTestimonialData} />
            </Box>
            <SolutionsAccordion
                heading="DeFi Solutions We Engineer for Enterprise-Grade Execution"
                subheading="Building a DeFi protocol demands more than <a target='_blank' href='/smart-contract-development'>smart contract development</a>. It requires liquidity architecture, compliance integration, cross-chain connectivity, and user-facing interfaces working as a unified system. Our DeFi engineering practice delivers each of these components through structured, outcome-driven engagements. The solutions below represent what enterprises, <a target='_blank' href='/blog/top-fintech-software-development-companies'>fintech firms</a>, and institutional investors commission when execution precision is non-negotiable."
                solutionsData={defiSolutionsData}
            />
            <Box className="top-space mobile-top-padding bottom-spacing mobile-bottom-padding">
                <WhyChooseUs
                    heading="Why Choose Debut Infotech For DeFi Development Services?"
                    subheading="If you are evaluating DeFi development services, the real question is not capability but execution certainty under financial and regulatory pressure. At Debut Infotech, we engineer decentralized financial infrastructure designed for institutional reliability, compliance alignment, and measurable performance outcomes."
                    topCardsData={defiTopCardsData}
                    checklistData={defiChecklistData}
                />
            </Box>
            <Box className="bottom-spacing mobile-padding-bottom">
                <CTA
                    heading={defiProtocolCTAData.heading}
                    subheading={defiProtocolCTAData.subheading}
                    bullets={defiProtocolCTAData.bullets}
                    buttonText={defiProtocolCTAData.buttonText}
                    imageSrc={defiProtocolCTAData.image}
                    imageAlt="DeFi Protocol Visual"
                    openForm={openForm}
                />
            </Box>
            <TopPlatforms
                heading="Partner with Debut Infotech and Launch Your DeFi Platform Like the Popular Ones"
                subheading="The growing prominence of DeFi development platforms in the current crypto market has inspired many entrepreneurs to consider launching their own applications. If you're one of them, why not consider building a DeFi platform similar to a leading one in the industry with us?"
                data={defiPartnershipData}
            />
            <Box className="top-space mobile-top-padding bottom-spacing mobile-bottom-padding bg-blue-background">
                <TechStack
                    heading="Tools & Technologies We Use To Develop DeFi Solutions"
                    subheading="Building a robust DeFi platform demands the right technology foundation — one engineered for security, scalability, and regulatory compliance. The tools and technologies selected for your solution directly determine its performance under load, resistance to vulnerabilities, and capacity to evolve as decentralized finance markets and compliance landscapes continue to mature."
                    techCategories={defiTechCategories}
                />
            </Box>
            <Box className="bg-blue-background">
                <ComplianceReady
                    heading="Compliance-Ready DeFi Solutions Built for Regulatory Alignment"
                    subheading="We understand regulatory exposure, and we design DeFi platforms that meet institutional standards while minimizing risk. Our solutions combine technical rigor with regulatory alignment, ensuring your platform is trusted, auditable, and ready for regulated participation."
                    data={defiComplianceData}
                />
            </Box>
            <Box className="top-space bottom-spacing mobile-padding-bottom bg-blue-background">
                <Experts heading="Meet Our Experts" expert={expertsData[0]} />
            </Box>
            <Box className="top-space bottom-spacing mobile-padding-bottom">
                <Process
                    heading="Enterprise-Grade DeFi Development Process and Governance Framework"
                    subheading="As an experienced DeFi development company, we do not treat DeFi development as a linear checklist of deliverables. It is an engineering cycle that moves from business clarity to protocol architecture, to controlled mainnet deployment, and then to continuous optimization under live market conditions."
                    processData={defiProcessData}
                />
            </Box>
            <hr className="hr-line zero-margin" />
            <Box className="top-space mobile-top-padding bottom-spacing mobile-padding-bottom">
                <Cost
                    heading={defiCostData.heading}
                    subheading={defiCostData.subheading}
                    rangeText={defiCostData.rangeText}
                    rangeValue={defiCostData.rangeValue}
                    rangeSubtext={defiCostData.rangeSubtext}
                    accordions={defiCostData.accordions}
                />
            </Box>
            <Box className="top-space mobile-top-padding bottom-spacing mobile-padding-bottom">
                <CTA
                    heading={defiFinalCTAData.heading}
                    subheading={defiFinalCTAData.subheading}
                    buttonText={defiFinalCTAData.buttonText}
                    imageSrc={defiFinalCTAData.image}
                    openForm={openForm}
                />
            </Box>
            <Box className="top-space mobile-top-padding bottom-spacing mobile-bottom-padding">
                <Awards heading="Awards & Accolades" awardsList={awardsList} />
            </Box>
            <Grid item xs={12} className="top-space mobile-top-padding">
                <MUIContainer>
                    <FAQ heading="FAQs on DeFi Development Services" faqData={defiFAQData} />
                </MUIContainer>
            </Grid>
            <Grid item xs={12} className="latest-insights top-space mobile-top-padding">
                <LatestInsights id="19" />
            </Grid>

            {/*
                NOTE: Two <script> tags removed from here:
                  - <script id="app-id-json"> — old Product schema
                  - <script id="decentralized-finance-defi-development"> — old FAQPage
                Also removed: 3 new scripts you provided (WebPage, BreadcrumbList, Product)
                All replaced with single pageSchema in <Head> above.
            */}
        </Box>
    );
};

export default DefiDevlopment;
DefiDevlopment.Layout = 'Main';
